import React, { useState, useRef, useEffect, memo } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { useOsWindowList, useOsActions } from "./OsContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faXmark, faChevronDown, faGear, faArrowRightFromBracket, faSidebarFlip, faLock } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { useAuth } from "@/context/AuthContext";
import WorkerAvatar from "@/components/worker/WorkerAvatar";
import { userAvatarSeed } from "@/utils/userDisplayName";
import { useOsNavigate } from "./OsLink";
import { ChangePasswordDialog } from "@/components/auth/ChangePasswordDialog";
import { UserAccountMenuIdentity } from "@/components/auth/UserAccountMenuIdentity";
import { MfaEnrollDialog } from "@/components/auth/MfaEnrollDialog";
import { DOCK_CHROME_GLASS_CLASSES } from "./osDockIconTheme";

interface OsWorkspaceBarProps {
  onToggleLayout?: () => void;
}

export const OsWorkspaceBar: React.FC<OsWorkspaceBarProps> = memo(function OsWorkspaceBar({
  onToggleLayout,
}) {
  const { workspaces, activeWorkspaceId } = useOsWindowList();
  const {
    addWorkspace,
    removeWorkspace,
    switchWorkspace,
    updateWorkspaceName,
  } = useOsActions();
  const [hoveredWorkspaceId, setHoveredWorkspaceId] = useState<string | null>(
    null
  );
  const [editingWorkspaceId, setEditingWorkspaceId] = useState<string | null>(
    null
  );
  const [editValue, setEditValue] = useState("");
  const [inputWidth, setInputWidth] = useState(0);
  const [originalWidth, setOriginalWidth] = useState(0);
  const editInputRef = useRef<HTMLInputElement>(null);
  const measureSpanRef = useRef<HTMLSpanElement>(null);
  const originalSpanRef = useRef<HTMLSpanElement>(null);
  const { user } = useAuth();
  const osNavigate = useOsNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [userMenuPosition, setUserMenuPosition] = useState({ top: 0, left: 0 });
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isMfaEnrollOpen, setIsMfaEnrollOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userMenuTriggerRef = useRef<HTMLButtonElement>(null);

  const startEditing = (workspaceId: string, currentName: string) => {
    setEditingWorkspaceId(workspaceId);
    setEditValue(currentName);
    if (originalSpanRef.current) {
      setOriginalWidth(originalSpanRef.current.offsetWidth);
      setInputWidth(originalSpanRef.current.offsetWidth);
    }
  };

  const saveEdit = () => {
    if (editingWorkspaceId) {
      const trimmedValue = editValue.trim();
      if (trimmedValue) {
        updateWorkspaceName(editingWorkspaceId, trimmedValue);
      }
    }
    setEditingWorkspaceId(null);
    setEditValue("");
  };

  const cancelEdit = () => {
    setEditingWorkspaceId(null);
    setEditValue("");
  };

  useEffect(() => {
    if (editingWorkspaceId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingWorkspaceId]);

  useEffect(() => {
    if (measureSpanRef.current && editValue) {
      const newWidth = measureSpanRef.current.offsetWidth;
      setInputWidth(Math.max(newWidth, originalWidth));
    }
  }, [editValue, originalWidth]);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        userMenuRef.current &&
        userMenuTriggerRef.current &&
        !userMenuRef.current.contains(event.target as Node) &&
        !userMenuTriggerRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
    }

    if (isUserMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isUserMenuOpen]);

  useEffect(() => {
    if (!isUserMenuOpen || !userMenuTriggerRef.current) return;

    const updatePosition = () => {
      if (!userMenuTriggerRef.current) return;
      const rect = userMenuTriggerRef.current.getBoundingClientRect();
      const menuWidth = 256;
      setUserMenuPosition({
        top: rect.bottom + 4,
        left: Math.max(8, rect.right - menuWidth),
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isUserMenuOpen]);

  const handleLogout = () => {
    window.location.href = "/logout";
  };

  return (
    <div
      className={`flex h-8 select-none items-center px-1.5 leading-none border-b border-slate-200/80 dark:border-white/10 ${DOCK_CHROME_GLASS_CLASSES}`}
    >
      <span
        ref={measureSpanRef}
        className="pointer-events-none invisible absolute whitespace-pre text-xs font-medium"
        aria-hidden="true"
      >
        {editValue}
      </span>
      <div className="no-scrollbar flex flex-1 items-center gap-0.5 overflow-x-auto">
        {workspaces.map((workspace) => {
          const isActive = workspace.id === activeWorkspaceId;
          const isHovered = hoveredWorkspaceId === workspace.id;
          const isEditing = editingWorkspaceId === workspace.id;

          return (
            <div
              key={workspace.id}
              className={clsx(
                "group relative flex cursor-pointer items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium transition-colors",
                isActive
                  ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/90 dark:bg-slate-800 dark:text-white dark:ring-0"
                  : "text-slate-600 hover:bg-slate-200/90 dark:text-slate-400 dark:hover:bg-slate-700/50",
              )}
              onClick={() => !isEditing && switchWorkspace(workspace.id)}
              onContextMenu={(e) => {
                e.preventDefault();
                if (!isEditing) startEditing(workspace.id, workspace.name);
              }}
              title={isEditing ? undefined : "Double-click or right-click to rename"}
              onMouseEnter={() => setHoveredWorkspaceId(workspace.id)}
              onMouseLeave={() => setHoveredWorkspaceId(null)}
            >
              {isEditing ? (
                <>
                  <span className="invisible px-0.5">{workspace.name}</span>
                  <input
                    ref={editInputRef}
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={saveEdit}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        saveEdit();
                      } else if (e.key === "Escape") {
                        cancelEdit();
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className={clsx(
                      "absolute left-1.5 top-1/2 -translate-y-1/2 rounded-sm border bg-transparent px-0.5 py-0 text-xs focus:outline-none",
                      "border-slate-300 focus:border-indigo-500 dark:border-slate-500 dark:focus:border-indigo-400",
                      isActive
                        ? "text-slate-900 dark:text-white"
                        : "text-slate-600 dark:text-slate-400",
                    )}
                    style={{ width: `${Math.max(inputWidth + 4, 72)}px` }}
                  />
                </>
              ) : (
                <span
                  ref={originalSpanRef}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    startEditing(workspace.id, workspace.name);
                  }}
                >
                  {workspace.name}
                </span>
              )}
              {isActive && workspaces.length > 1 && !isEditing && (
                <button
                  type="button"
                  data-testid="os-workspace-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeWorkspace(workspace.id);
                  }}
                  className="rounded-full p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-600 dark:hover:text-slate-200"
                >
                  <FontAwesomeIcon icon={faXmark} className="h-2.5 w-2.5" />
                </button>
              )}
            </div>
          );
        })}
        <button
          type="button"
          onClick={addWorkspace}
          className="rounded-md p-0.5 text-slate-500 transition-colors hover:bg-slate-200/90 hover:text-slate-700 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-300"
          title="Add Workspace"
        >
          <FontAwesomeIcon icon={faPlus} className="h-2.5 w-2.5" />
        </button>
      </div>
      
      {user && (
        <div className="relative ml-2">
          <button
            ref={userMenuTriggerRef}
            type="button"
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="flex items-center gap-1.5 rounded-md px-1 py-0.5 text-xs text-slate-600 tabular-nums transition-colors hover:bg-slate-200/90 dark:text-slate-400 dark:hover:bg-slate-700"
            aria-expanded={isUserMenuOpen}
            aria-haspopup="menu"
          >
            <span className="whitespace-nowrap">
              {currentTime.toLocaleDateString("en-US", { weekday: "short" })}{" "}
              {currentTime.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })}{" "}
              {currentTime.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
            <WorkerAvatar
              key={user.id}
              seed={userAvatarSeed(user)}
              size={18}
              image={user.image}
              user={true}
            />
            <FontAwesomeIcon
              icon={faChevronDown}
              className={`h-2 w-2 transition-transform ${isUserMenuOpen ? "rotate-180" : ""}`}
            />
          </button>

          {isUserMenuOpen &&
            createPortal(
              <div
                ref={userMenuRef}
                style={{
                  position: "fixed",
                  top: `${userMenuPosition.top}px`,
                  left: `${userMenuPosition.left}px`,
                  zIndex: 10060,
                }}
                className="w-64 rounded-2xl bg-white shadow-lg shadow-slate-900/10 ring-1 ring-slate-200 dark:bg-slate-900/95 dark:shadow-none dark:ring-white/10"
                role="menu"
              >
                <div className="p-2">
                  <UserAccountMenuIdentity user={user} />
                  <button
                    onClick={() => {
                      osNavigate("/settings", "Settings");
                      setIsUserMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors"
                  >
                    <FontAwesomeIcon icon={faGear} className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                    <span className="flex-1 text-left">Settings</span>
                  </button>
                  {onToggleLayout && (
                    <button
                      onClick={() => {
                        onToggleLayout();
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors"
                    >
                      <FontAwesomeIcon icon={faSidebarFlip} className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                      <span className="flex-1 text-left">Classic layout</span>
                    </button>
                  )}
                  {user?.sso_enabled !== true && (
                    <>
                    <button
                      onClick={() => {
                        setIsChangePasswordOpen(true);
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors"
                      data-testid="os-change-password-menu-item"
                    >
                      <FontAwesomeIcon icon={faLock} className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                      <span className="flex-1 text-left">Change password</span>
                    </button>
                    <button
                      onClick={() => {
                        setIsMfaEnrollOpen(true);
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors"
                      data-testid="os-mfa-enroll-menu-item"
                    >
                      <FontAwesomeIcon icon={faLock} className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                      <span className="flex-1 text-left">Authenticator app</span>
                    </button>
                    </>
                  )}
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors"
                  >
                    <FontAwesomeIcon icon={faArrowRightFromBracket} className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                    <span className="flex-1 text-left">Logout</span>
                  </button>
                </div>
              </div>,
              document.body,
            )}
        </div>
      )}
      {isChangePasswordOpen && (
        <ChangePasswordDialog
          isOpen={isChangePasswordOpen}
          onClose={() => setIsChangePasswordOpen(false)}
        />
      )}
      {isMfaEnrollOpen && (
        <MfaEnrollDialog
          isOpen={isMfaEnrollOpen}
          onClose={() => setIsMfaEnrollOpen(false)}
        />
      )}
    </div>
  );
});
